import Pusher, { type Channel } from 'pusher-js';

// pusher-js doesn't export its ConnectionState string union.
export type ReadyState =
    | 'initialized'
    | 'connecting'
    | 'connected'
    | 'unavailable'
    | 'failed'
    | 'disconnected';

const key = (import.meta.env.VITE_REVERB_APP_KEY ?? '') as string;
const host = (import.meta.env.VITE_REVERB_HOST ?? 'localhost') as string;
const port = Number(import.meta.env.VITE_REVERB_PORT ?? 8081);
const scheme = (import.meta.env.VITE_REVERB_SCHEME ?? 'http') as string;

let singleton: Pusher | null = null;

/**
 * Lazy, process-wide Pusher (Reverb-protocol) connection. We keep a single
 * Pusher instance so reconnect logic and channel subscriptions are shared
 * across components — calling `getPusher()` from multiple GameRoom mounts
 * during the session does not multiply sockets.
 */
export function getPusher(): Pusher {
    if (singleton) {
        return singleton;
    }

    if (!key) {
        throw new Error('VITE_REVERB_APP_KEY is not set; cannot connect to Reverb.');
    }

    singleton = new Pusher(key, {
        wsHost: host,
        wsPort: port,
        wssPort: port,
        forceTLS: scheme === 'https',
        enabledTransports: ['ws', 'wss'],
        // Reverb ignores Pusher clusters; keep a dummy value because
        // pusher-js requires it, while wsHost above pins the real target.
        cluster: 'reverb',
        disableStats: true,
        activityTimeout: 30_000,
        pongTimeout: 10_000,
    });

    return singleton;
}

export function onConnectionState(handler: (state: { previous: ReadyState; current: ReadyState }) => void): () => void {
    const pusher = getPusher();
    pusher.connection.bind('state_change', handler);
    return () => pusher.connection.unbind('state_change', handler);
}

export function subscribe(channelName: string): Channel {
    return getPusher().subscribe(channelName);
}

export function unsubscribe(channelName: string): void {
    if (singleton) {
        singleton.unsubscribe(channelName);
    }
}
